import { onMounted, Ref, ref } from 'vue';
import { inBrowser } from '@mts-fe/utils';

type ScrollElement = HTMLElement | Window;

const overflowScrollReg = /scroll|auto|overlay/i;
const defaultRoot = inBrowser ? window : undefined;

function isElement(node: Element) {
  const ELEMENT_NODE_TYPE = 1;
  return node.tagName !== 'HTML' && node.tagName !== 'BODY' && node.nodeType === ELEMENT_NODE_TYPE;
}

export function getScrollParent(el: Element, root: ScrollElement | undefined = defaultRoot) {
  let node = el;

  while (node && node !== root && isElement(node)) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowScrollReg.test(overflowY)) {
      return node;
    }
    node = node.parentNode as Element;
  }

  return root;
}

export function useScrollParent(
  el: Ref<Element | undefined>,
  root: ScrollElement | undefined = defaultRoot,
) {
  const scrollParent = ref<Element | Window>();

  onMounted(() => {
    if (el.value) {
      scrollParent.value = getScrollParent(el.value, root);
    }
  });

  return scrollParent;
}

/**
 * 向上查找真正在滚动的元素（scrollHeight > clientHeight）
 * 仅 CSS overflow:auto 不代表元素实际在滚动，height:100% 可能解析为内容高度导致无溢出
 * @param startEl 起始元素
 * @param root 查找的根节点，默认 document.body
 * @returns 第一个实际在滚动的元素，找不到则返回 null
 */
export function findScrollingElement(
  startEl: HTMLElement,
  root: HTMLElement | null = null,
): HTMLElement | null {
  let el: HTMLElement | null = startEl;
  const rootEl = root ?? document.body;
  while (el && el !== rootEl) {
    if (el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}
